import { Component } from '@theme/component';

/**
 * Manual review carousel: native touch scrolling, no autoplay or cloned reviews.
 * @typedef {{track: HTMLDivElement, controls: HTMLDivElement, previous: HTMLButtonElement, next: HTMLButtonElement, status: HTMLSpanElement}} ReviewRefs
 * @extends {Component<ReviewRefs>}
 */
class TimpaniTestimonials extends Component {
  requiredRefs = ['track', 'controls', 'previous', 'next', 'status'];
  #abort;
  #resize;
  #frame = 0;
  #announceTimer = 0;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#announceTimer);
    this.#frame = 0;
  }

  #setup() {
    this.#cleanup();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.refs.track.addEventListener('scroll', () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#measure();
      });
      clearTimeout(this.#announceTimer);
      this.#announceTimer = setTimeout(() => this.#announce(), 160);
    }, { passive: true, signal });
    this.refs.track.addEventListener('keydown', this.#key, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      const card = this.#cards().find(item => item.dataset.blockId === event.detail?.blockId);
      if (card) this.#go(this.#position(card));
    }, { signal });
    this.#resize = new ResizeObserver(() => this.#measure());
    this.#resize.observe(this.refs.track);
    this.#measure();
  }

  #cards() { return [...this.refs.track.querySelectorAll('[data-tb93-card]')]; }
  #max() { return Math.max(0, this.refs.track.scrollWidth - this.refs.track.clientWidth); }

  #position(card) {
    const track = this.refs.track;
    const inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    return Math.max(0, Math.min(this.#max(), card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - inset));
  }

  #measure() {
    const { track, controls, previous, next } = this.refs;
    const max = this.#max();
    const overflow = max > 2;
    controls.hidden = !overflow;
    track.tabIndex = overflow ? 0 : -1;
    this.dataset.overflow = String(overflow);
    previous.disabled = !overflow || track.scrollLeft <= 2;
    next.disabled = !overflow || track.scrollLeft >= max - 2;
  }

  #go(left) {
    this.refs.track.scrollTo({ left, behavior: this.#reduced.matches ? 'auto' : 'smooth' });
  }

  /** Move one review backward using the existing Previous control. */
  previous() {
    const left = this.refs.track.scrollLeft;
    const position = this.#cards().map(card => this.#position(card)).filter(value => value < left - 2).at(-1);
    this.#go(position ?? 0);
  }

  /** Move one review forward using the existing Next control. */
  next() {
    const left = this.refs.track.scrollLeft;
    const position = this.#cards().map(card => this.#position(card)).find(value => value > left + 2);
    this.#go(position ?? this.#max());
  }

  #key = (event) => {
    if (event.target !== this.refs.track) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'ArrowLeft') this.previous();
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'Home') this.#go(0);
    if (event.key === 'End') this.#go(this.#max());
  };

  #announce() {
    const track = this.refs.track;
    const cards = this.#cards();
    if (!cards.length || this.#max() <= 2) return;
    const inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const left = track.getBoundingClientRect().left + inset;
    const index = cards.reduce((best, card, current) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cards[best].getBoundingClientRect().left - left) ? current : best, 0);
    this.refs.status.textContent = this.dataset.positionLabel.replace('[number]', String(index + 1)).replace('[total]', String(cards.length));
  }
}

if (!customElements.get('timpani-testimonials')) customElements.define('timpani-testimonials', TimpaniTestimonials);
